import type {
  AbsoluteVerticalDatumEntity,
  LegacyFoundationLateralDatumEntity,
  LegacyFoundationVerticalDatumEntity,
  LegacyLevelDatumEntity,
  LegacyWallHorizontalDatumEntity,
  LegacyWallPlaneHorizontalDatumEntity,
  LegacyWallVerticalDatumEntity,
} from "building-types/datums/datumEntityTypes";
import { DatumDirection, DatumOffsetType, DatumType } from "building-types/datums/datumEntityTypes";
import { FoundationLateralDatumAnchor } from "building-types/datums/datumsTypes";
import {
  FoundationDatumVerticalAnchor,
  HorizontalJustification,
  LateralDatumJustification,
  LevelDatumAnchor,
  VerticalJustification,
  WallHorizontalDatum,
  WallVerticalDatumAnchor,
} from "building-types/datums/datumsTypesEnum";
import uid from "common/utils/uid";

/**
 * Creates a new LegacyLevelDatumEntity with sensible defaults.
 */
export function levelDatumEntityNew({
  sortIndex = 0,
  id = uid(),
  name = "Level Datum",
  justification = VerticalJustification.BOTTOM_UP,
  anchor = LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
  levelId,
  offset,
  offsetNormalized,
}: Partial<LegacyLevelDatumEntity>): LegacyLevelDatumEntity {
  return {
    id,
    sortIndex,
    name,
    entityType: "DATUM",
    datumType: DatumType.LEVEL,
    direction: DatumDirection.VERTICAL,
    offsetType: DatumOffsetType.RELATIVE,
    justification,
    anchor,
    levelId,
    offset,
    offsetNormalized,
  };
}

/**
 * Creates a new LegacyFoundationVerticalDatumEntity with sensible defaults.
 */
export function foundationVerticalDatumEntityNew({
  sortIndex = 0,
  id = uid(),
  name = "Foundation Vertical Datum",
  justification = VerticalJustification.BOTTOM_UP,
  anchor = FoundationDatumVerticalAnchor.FOUNDATION,
  offset,
  offsetNormalized,
}: Partial<LegacyFoundationVerticalDatumEntity>): LegacyFoundationVerticalDatumEntity {
  return {
    id,
    sortIndex,
    name,
    entityType: "DATUM",
    datumType: DatumType.FOUNDATION,
    direction: DatumDirection.VERTICAL,
    offsetType: DatumOffsetType.RELATIVE,
    justification,
    anchor,
    offset,
    offsetNormalized,
  };
}

/**
 * Creates a new LegacyFoundationLateralDatumEntity with sensible defaults.
 */
export function foundationLateralDatumEntityNew({
  sortIndex = 0,
  id = uid(),
  name = "Foundation Lateral Datum",
  justification = LateralDatumJustification.INSIDE_TO_OUTSIDE,
  anchor = FoundationLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
  offset,
  offsetNormalized,
}: Partial<LegacyFoundationLateralDatumEntity>): LegacyFoundationLateralDatumEntity {
  return {
    id,
    sortIndex,
    name,
    entityType: "DATUM",
    datumType: DatumType.FOUNDATION,
    direction: DatumDirection.LATERAL,
    offsetType: DatumOffsetType.RELATIVE,
    justification,
    anchor,
    offset,
    offsetNormalized,
  };
}

/**
 * Creates a new LegacyWallVerticalDatumEntity with sensible defaults.
 */
export function wallVerticalDatumEntityNew({
  sortIndex = 0,
  id = uid(),
  name = "Facade Vertical Datum",
  justification = VerticalJustification.BOTTOM_UP,
  anchor = WallVerticalDatumAnchor.BASE_WALL,
  offset,
  offsetNormalized,
}: Partial<LegacyWallVerticalDatumEntity>): LegacyWallVerticalDatumEntity {
  return {
    id,
    sortIndex,
    name,
    entityType: "DATUM",
    datumType: DatumType.WALL,
    direction: DatumDirection.VERTICAL,
    offsetType: DatumOffsetType.RELATIVE,
    justification,
    anchor,
    offset,
    offsetNormalized,
  };
}

/**
 * Creates a new LegacyWallHorizontalDatumEntity with sensible defaults.
 */
export function wallPlaneHorizontalDatumEntityNew({
  sortIndex = 0,
  id = uid(),
  name = "Wall Plane Horizontal Datum",
  justification = HorizontalJustification.START_TO_END,
  planeHash = "",
  offset = 0,
  datumScope,
}: Partial<LegacyWallPlaneHorizontalDatumEntity>): LegacyWallPlaneHorizontalDatumEntity {
  return {
    id,
    sortIndex,
    name,
    entityType: "DATUM",
    datumType: DatumType.WALL_PLANE,
    direction: DatumDirection.HORIZONTAL,
    offsetType: DatumOffsetType.ABSOLUTE,
    justification,
    planeHash,
    offset,
    ...(datumScope !== undefined ? { datumScope } : {}),
  };
}

export function wallHorizontalDatumEntityNew({
  sortIndex = 0,
  id = uid(),
  name = "Facade Horizontal Datum",
  justification = HorizontalJustification.START_TO_END,
  anchor = WallHorizontalDatum.ROOM_EDGE,
  offset,
  offsetNormalized,
}: Partial<LegacyWallHorizontalDatumEntity>): LegacyWallHorizontalDatumEntity {
  return {
    id,
    sortIndex,
    name,
    entityType: "DATUM",
    datumType: DatumType.WALL,
    direction: DatumDirection.HORIZONTAL,
    offsetType: DatumOffsetType.RELATIVE,
    justification,
    anchor,
    offset,
    offsetNormalized,
  };
}

/**
 * Creates a new AbsoluteVerticalDatumEntity with sensible defaults.
 */
export function absoluteVerticalDatumEntityNew({
  sortIndex = 0,
  id = uid(),
  name = "Origin Relative Datum",
  justification = VerticalJustification.BOTTOM_UP,
  offset,
  offsetNormalized,
  datumScope,
}: Partial<AbsoluteVerticalDatumEntity>): AbsoluteVerticalDatumEntity {
  return {
    id,
    sortIndex,
    name,
    entityType: "DATUM",
    datumType: DatumType.SHAREABLE,
    direction: DatumDirection.VERTICAL,
    offsetType: DatumOffsetType.ABSOLUTE,
    justification,
    offset,
    offsetNormalized,
    ...(datumScope !== undefined ? { datumScope } : {}),
  };
}
